//
// LegalAcceptanceGate
//
// Blocks UI with a CGU acceptance popup if the user hasn't accepted
// the current major version. legalRevLoader keeps retrying until the
// versions arrive, so the popup may appear well after the app has loaded.
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import Markdown from 'react-markdown';
import superagent from 'superagent';

import {
  useAuth,
  useTownAssets,
} from '../stores';
import {
  config,
  logError,
} from '../utils';
import useLegalRev from '../utils/legalRevLoader';

import { Button } from './buttons';
import {
  Column,
  Row,
} from './display';
import FullPopup from './display/FullPopup';
import Checkbox from './input/Checkbox';
import SupportLink from './SupportLink';

const majorVersion = (v) => Number(v?.split('.')?.[0]);

const fetchLegalContent = async (document, lang) => {
  try {
    const url = config.town.back.urlPrefix
              + config.town.back.legal[document](lang);

    const res = await superagent.get(url);
    const content = res.body?.rslt?.data?.[0];

    if ((!content) || (content.legalUnavailable)) {
      logError(
        `LegalAcceptanceGate fetchContent(${document}, ${lang}): documents unavailable`,
        'LegalAcceptanceGate.unavailable',
        {
          document,
          lang,
        },
      );
      return null;
    }

    return content;
  } catch (err) {
    logError(
      `LegalAcceptanceGate fetchContent(${document}, ${lang}): ${err.status ?? 'network error'}`,
      'LegalAcceptanceGate.fetchContent.error',
      {
        document,
        lang,
        err: err?.message,
        ...(err?.status && { status: err.status }),
      },
    );
    return null;
  }
};

const LegalAcceptanceGate = function LegalAcceptanceGate({ children }) {
  const { auth } = useAuth();
  const {
    info: userInfo,
    addUserSetting,
  } = auth;
  const { assets } = useTownAssets();
  const { translate, language } = assets;
  const lang = language?.language ?? 'fr';

  const [cguContent, setCguContent] = useState(null);
  const [privacyContent, setPrivacyContent] = useState(null);
  const [pageState, setPageState] = useState('init');
  const [acceptChecked, setAcceptChecked] = useState(false);

  const serverVersions = useLegalRev();

  const userLegal = userInfo?.settings?.user?.legal?.rev;

  const needsAcceptance = userInfo
                          && serverVersions
                          && ((majorVersion(userLegal?.cgu)
                                    !== majorVersion(serverVersions.cgu))
                            || (majorVersion(userLegal?.privacy)
                                    !== majorVersion(serverVersions.privacy)));

  // console.log('LegalAcceptanceGate:', JSON.stringify({
  //   hasUserInfo: !!userInfo,
  //   serverVersions,
  //   userLegal,
  //   needsAcceptance,
  //   pageState,
  //   settingsUser: userInfo?.settings?.user,
  //   settingsDevice: userInfo?.settings?.device,
  //   settingsAll: userInfo?.settings,
  // }));

  useEffect(
    () => {
      if (needsAcceptance && (pageState === 'init')) {
        setPageState('fetching');

        // Use void to make it clear we do not want to wait this async function:
        void Promise.all([
          fetchLegalContent('cgu', lang),
          fetchLegalContent('privacy', lang),
        ]).then(([cgu, privacy]) => {
          if (cgu && privacy) {
            setCguContent(cgu);
            setPrivacyContent(privacy);
            setPageState('fetched');
          } else {
            setPageState('fetchError');
          }
        });
      }
    },
    [pageState, needsAcceptance, lang],
  );

  const handleAccept = useCallback(
    async () => {
      if ((!serverVersions) || (pageState === 'accepting')) return;

      setPageState('accepting');

      const legal = {
        rev: {
          cgu: serverVersions.cgu,
          privacy: serverVersions.privacy,
        },
        accepted: new Date().toISOString(),
        channel: 'town',
      };

      await addUserSetting({ legal });
    },
    [serverVersions, addUserSetting, pageState],
  );

  if (!needsAcceptance) {
    return children;
  }

  if (pageState === 'fetchError') {
    return (
      <Column className="min-h-screen items-center justify-center text-center p-4" gap={3}>
        <p>{translate('errorLoadingLegalDocuments')}</p>
        <SupportLink />
      </Column>
    );
  }

  return (
    <FullPopup
      titleBar={false}
      disableBackgroundClick
    >
      <Column className="max-h-[70vh] w-full">
        <p className="px-4 py-3 text-md italic">
          {translate('legalAcceptancePrompt', 'statements')}
        </p>

        <div className="overflow-y-auto flex-1 px-5 py-6">
          <div className="px-md-preview shadow-lg rounded-lg border border-pxMainOutline p-4 mb-8">
            <Markdown>{cguContent}</Markdown>
          </div>

          <div className="px-md-preview shadow-lg rounded-lg border border-pxMainOutline p-4">
            <Markdown>{privacyContent}</Markdown>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-4 py-3 border-t border-pxBorder">
          <Row start>
            <Checkbox
              checked={acceptChecked}
              onChange={() => setAcceptChecked((prev) => (!prev))}
            />
            <span className="whitespace-normal text-start text-sm">
              {translate('legalAcceptCheckbox', 'statements')}
            </span>
          </Row>

          <div className="flex justify-center">
            <Button
              label={translate('accept')}
              id="acceptLegal"
              onClick={handleAccept}
              disabled={(pageState !== 'fetched') || (!acceptChecked)}
              baseColor
            />
          </div>
        </div>
      </Column>
    </FullPopup>
  );
};

LegalAcceptanceGate.propTypes = {
  children: PropTypes.node,
};

export default LegalAcceptanceGate;
